import { memo, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import ArrowSvg from '@/shared/assets/icons/ui-kit/breadcrumb-arrow.svg';
import GlobSvg from '@/shared/assets/icons/ui-kit/breadcrumb-glob.svg';
import { getLabel } from '@/shared/ui/breadcrumbs/breadcrumbs.data';

import * as Styled from './breadcrumbs.styles';
import { BreadcrumbOverride, BreadcrumbReduce, BreadcrumbsLocationState, BreadcrumbsProps } from './breadcrumbs.types';

const BreadcrumbsComponent = ({ className, paragraph, items }: BreadcrumbsProps) => {
	const { pathname, state } = useLocation();
	const breadcrumbSegments = pathname.split('/').filter((path) => Boolean(path) && !Number(path));

	const defaultCrumbs = useMemo(
		() =>
			breadcrumbSegments.reduce<BreadcrumbReduce[]>(
				(crumbs, crumb, index) =>
					index
						? [...crumbs, { to: `${crumbs[index - 1].to}/${crumb}`, label: getLabel(crumb), Svg: ArrowSvg }]
						: [{ to: `/${crumb}`, label: getLabel(crumb), Svg: GlobSvg }],
				[],
			),
		[pathname],
	);

	const customCrumbs = (state as BreadcrumbsLocationState)?.breadcrumbs;
	const breadcrumbOverrides = (state as BreadcrumbsLocationState)?.breadcrumbOverrides ?? [];

	const crumbs = useMemo(() => {
		if (items?.length) {
			return items;
		}

		if (customCrumbs?.length) {
			return customCrumbs;
		}

		if (!breadcrumbOverrides.length) {
			return defaultCrumbs;
		}

		const overridesMap = new Map<string, BreadcrumbOverride>(
			breadcrumbOverrides.map((override) => [override.segment, override]),
		);

		return defaultCrumbs.map((crumb, index) => {
			const segment = breadcrumbSegments[index] ?? '';
			const override = overridesMap.get(segment);

			if (!override) {
				return crumb;
			}

			const overrideProps = {
				...(override.to && { to: override.to }),
				...(override.label && { label: override.label }),
			};

			return { ...crumb, ...overrideProps, Svg: crumb.Svg };
		});
	}, [breadcrumbOverrides, pathname, customCrumbs, defaultCrumbs, items]);

	return (
		<Styled.Wrapper className={className} marginBottom={paragraph} tag="nav" align="center">
			{crumbs.map(({ to, label, Svg }, index) => (
				<Styled.BreadcrumbLink
					active={index === crumbs.length - 1}
					key={to}
					to={to}
					label={label}
					Svg={Svg ?? (index ? ArrowSvg : GlobSvg)}
				/>
			))}
		</Styled.Wrapper>
	);
};

export default memo(BreadcrumbsComponent);
